import { get as getConfig } from '../../config/index'

/**
 * Rate limit state for a single mode (either text or voice).
 */
export interface RateLimitModeState {
  remainingInteractions: number
  rateLimited: boolean
  refetchRateLimit: () => void
}

/**
 * Include the RateLimitModeState for text, embeds a value for voice, and tells
 * you if rate limiting is even a thing for this user.
 */
export interface RateLimitState extends RateLimitModeState {
  isRateLimitApplicable: boolean
  voiceRateLimitState?: RateLimitModeState
  limitType?: string | null
}

export type SubRateLimitState = {
  text: {
    remainingInteractions: number
    rateLimited: boolean
    totalInteractions: number
  } | null
  voice: {
    remainingInteractions: number
    rateLimited: boolean
    totalInteractions: number
  } | null
}

/**
 * GraphQL rate limit status structure from queries
 */
export interface RateLimitQueryData {
  rateLimitStatus?: {
    remainingInteractions: number
    rateLimited: boolean
    limitType?: string | null
  }
}

/**
 * Determines if rate limiting should be applied.
 *
 * Users with Oracle access (subscription codes OR Brooklyn/a la carte): no rate limiting.
 * Users without Oracle access: trial rate limiting when FEATURES_AI_ORACLE_TRIAL is on.
 */
export function shouldApplyRateLimit (options?: {
  hasOracleAccess?: boolean
}): boolean {
  // Users with oracle access: no rate limiting on the frontend
  if (options?.hasOracleAccess) {
    return false
  }

  // Users without oracle access: trial rate limiting if flag is on
  return !!getConfig().features.ai.oracleTrial
}

/**
 * Determines the color for rate limit warning text based on remaining interactions
 */
export function getRateLimitWarningColor (remainingInteractions: number): 'orange' | 'red' {
  return remainingInteractions <= 3 ? 'red' : 'orange'
}

/**
 * Gets the user's timezone for rate limit queries
 */
export function getUserTimezone (): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    // Fallback to UTC if timezone detection fails
    return 'UTC'
  }
}

/**
 * Calculates milliseconds until next midnight in user's timezone
 */
export function getMillisecondsUntilMidnight (): number {
  const now = new Date()
  const tomorrow = new Date(now)
  tomorrow.setDate(tomorrow.getDate() + 1)
  tomorrow.setHours(0, 0, 0, 0)
  return tomorrow.getTime() - now.getTime()
}

/**
 * Schedules a callback to run at the next midnight
 * Returns a cleanup function to cancel the scheduled callback
 */
export function scheduleNextMidnightReset (callback: () => void): () => void {
  const msUntilMidnight = getMillisecondsUntilMidnight()

  const timeoutId = setTimeout(() => {
    callback()
    // Schedule the next midnight reset
    scheduleNextMidnightReset(callback)
  }, msUntilMidnight)

  // Return cleanup function
  return () => clearTimeout(timeoutId)
}

export function buildRateLimitState ({
  isRateLimitApplicable,
  subscriptionState,
  textQueryData,
  voiceQueryData,
  refetchText,
  refetchVoice,
}: {
  isRateLimitApplicable: boolean
  subscriptionState: SubRateLimitState
  textQueryData: RateLimitQueryData | undefined
  voiceQueryData: RateLimitQueryData | undefined
  refetchText: () => void
  refetchVoice: () => void
}): RateLimitState {
  return isRateLimitApplicable ? {
    remainingInteractions: subscriptionState.text?.remainingInteractions
      ?? textQueryData?.rateLimitStatus?.remainingInteractions
      ?? 10,
    rateLimited: subscriptionState.text?.rateLimited
      ?? textQueryData?.rateLimitStatus?.rateLimited
      ?? false,
    isRateLimitApplicable: true,
    refetchRateLimit: refetchText,
    limitType: textQueryData?.rateLimitStatus?.limitType ?? null,
    voiceRateLimitState: {
      remainingInteractions: subscriptionState.voice?.remainingInteractions
        ?? voiceQueryData?.rateLimitStatus?.remainingInteractions
        ?? 100,
      rateLimited: subscriptionState.voice?.rateLimited
        ?? voiceQueryData?.rateLimitStatus?.rateLimited
        ?? false,
      refetchRateLimit: refetchVoice,
    },
  } : {
    remainingInteractions: 10,
    rateLimited: false,
    isRateLimitApplicable: false,
    refetchRateLimit: () => {}, // No-op for non-applicable users
    limitType: null,
    voiceRateLimitState: {
      remainingInteractions: 100,
      rateLimited: false,
      refetchRateLimit: () => {},
    },
  }
}
